// One authorized collection, presented as Pond or Explorer. Invitations retain their event through auth.
import { api, type Entitlements, type Lilypad, type Media, type PondTheme, type Retention, type Rules, type User } from './api';
import { LILY, sanitizeStyle } from './pond/avatar';
import { PondScene, type PadData } from './pond/scene';
import { sound } from './pond/sound';
import { createSoundControls } from './ui/sound-controls';
import { createMotionControls } from './ui/motion-controls';
import { createExplorer, type ExplorerElement } from './ui/explorer';
import { uploadFlow } from './ui/upload-flow';
import { h, showPanel, closePanel, toast, errMsg, joinPanel, loginPanel, verifyPanel, lilypadPanel, mediaPanel, leafPanel, createLilypadPanel, subscribePanel, sharingPanel, messagesPanel, requestCreatePanel, confirmContactPanel, fmtDate, type Session } from './ui/panels';

type Presentation = 'pond' | 'explorer';
type Section = 'photos' | 'people';
type View = { kind: 'pond' } | { kind: 'lilypad'; lp: Lilypad; section?: Section } | { kind: 'upload'; lp: Lilypad } | { kind: 'media'; lp: Lilypad; m: Media; all: Media[] } | { kind: 'leaf'; lp: Lilypad; leafId: string };
type JoinTarget = { id: string; slug: string; title: string; event_date: string; owner_first: string };

const scene = new PondScene();
scene.setPresented(false);
const soundControls = createSoundControls();
const motionControls = createMotionControls(scene);
const canvas = document.getElementById('pond') as HTMLCanvasElement;
const explorerRoot = document.getElementById('explorer-root')!;
const hud = document.getElementById('hud')!;
let sceneReady = false, sceneStarting: Promise<void> | null = null;
let explorer: ExplorerElement | null = null;
let presentation: Presentation = 'pond';
let pondTheme: PondTheme | null = null;
let hostTheme: PondTheme | null = null;
let retention: Retention | null = null;
let askedConfirm = false;
let introHandle: { walkOff: () => void } | null = null;
let rules: Rules;
let user: User | null = null;
let ent: Entitlements | null = null;
let lilypads: Lilypad[] = [];
let pond: { id: string; name: string } | null = null;
let baseDomain = location.hostname;
let view: View = { kind: 'pond' };
let joinTarget: JoinTarget | null = null;
let collectionLoading = false, collectionError: string | null = null;
let loadVersion = 0;
let disposed = false;

const verified = () => !!user && !!user.email_verified_at && !!user.phone_verified_at;
const modeKey = (id: string) => `lilypad.presentation.v1:${id}`;
function preferredMode(id: string): Presentation {
  try { return localStorage.getItem(modeKey(id)) === 'explorer' ? 'explorer' : 'pond'; } catch { return 'pond'; }
}
function rememberInvitation(): void {
  if (!joinTarget) return;
  try { sessionStorage.setItem('lilypad.pending-invite.v1', JSON.stringify({ id: joinTarget.id, slug: joinTarget.slug, at: Date.now() })); } catch { /* The current host/path still carries this invitation. */ }
}
function clearInvitation(): void {
  try { sessionStorage.removeItem('lilypad.pending-invite.v1'); } catch { /* No persistent browser storage required. */ }
}
function applyCharacter(): void {
  if (!sceneReady) return;
  scene.setCharacter(pondTheme?.character === 'lily' ? sanitizeStyle(pondTheme.characterStyle ?? null, LILY) : null);
}
function updatePads(): void {
  if (!sceneReady) return;
  const pads: PadData[] = lilypads.map((lp) => ({ id: lp.id, title: lp.title, dateLabel: fmtDate(lp.event_date), subtitle: `${lp.media_count} photos & videos`, x: lp.pos_x, y: lp.pos_y, size: lp.size, mine: lp.mine, coverStyle: lp.cover_style, coverUrl: lp.cover_media_id ? api.thumbUrl(lp.cover_media_id) : undefined }));
  if (joinTarget && !lilypads.some((lp) => lp.id === joinTarget!.id)) pads.push({ id: joinTarget.id, title: joinTarget.title, dateLabel: fmtDate(joinTarget.event_date), subtitle: '', x: 0.5, y: 0.5, size: 1.15, mine: false, locked: true });
  scene.setLilypads(pads);
}
async function ensureScene(): Promise<void> {
  if (sceneReady || disposed) return;
  if (!sceneStarting) sceneStarting = scene.init(canvas).then(() => {
    if (disposed) return;
    sceneReady = true; applyCharacter(); updatePads();
    scene.setPresented(presentation === 'pond');
    if (view.kind !== 'pond') scene.focusPad(view.lp.id, false);
  }).catch((error) => { sceneStarting = null; throw error; });
  await sceneStarting;
}
function measureHud(): void {
  document.documentElement.style.setProperty('--explorer-top', `${Math.ceil(hud.getBoundingClientRect().bottom + 8)}px`);
}
const hudObserver = new ResizeObserver(measureHud);
hudObserver.observe(hud);
window.addEventListener('resize', measureHud);

function synchronizePresentation(): void {
  const inExplorer = presentation === 'explorer' && verified();
  document.body.dataset.presentation = inExplorer ? 'explorer' : 'pond';
  document.body.classList.toggle('explorer-view', inExplorer);
  canvas.hidden = inExplorer;
  explorerRoot.hidden = !inExplorer;
  // The existing #pond display rule outranks the browser's default [hidden] rule.
  canvas.style.display = inExplorer ? 'none' : 'block';
  explorerRoot.style.display = inExplorer ? 'block' : 'none';
  scene.setPresented(!inExplorer);
  if (inExplorer && user) {
    if (!explorer) {
      explorer = createExplorer({ user, lilypads, loading: collectionLoading, error: collectionError,
        onOpen: (lp, section) => go({ kind: 'lilypad', lp, section }), onCreate: openCreate,
        onAccount: () => showPanel(accountPanel()), onReload: async () => { await loadPond(); return lilypads; } });
      explorerRoot.replaceChildren(explorer);
    } else explorer.update({ lilypads, loading: collectionLoading, error: collectionError });
    explorer.setPresented(true);
  } else {
    explorer?.setPresented(false);
    delete document.documentElement.dataset.explorerTheme;
    void ensureScene().catch((error) => toast(`Could not open the pond: ${errMsg(error)}`, 'error'));
  }
  measureHud();
}
function setPresentation(mode: Presentation): boolean {
  if (!verified() || !closePanel()) return false;
  presentation = mode;
  try { if (user) localStorage.setItem(modeKey(user.id), mode); } catch { /* In-session switching remains available. */ }
  introHandle?.walkOff();
  synchronizePresentation(); renderHud();
  return true;
}

const session: Session = {
  get rules() { return rules; }, get user() { return user; }, get ent() { return ent; },
  refresh: async () => {
    const oldUser = user?.id, wasVerified = verified();
    try { const me = await api.me(); user = me.user; ent = me.entitlements; retention = me.retention; }
    catch { user = null; ent = null; retention = null; }
    if (oldUser !== user?.id) {
      explorer?.cleanup(); explorer = null; explorerRoot.replaceChildren();
      askedConfirm = false; view = { kind: 'pond' };
      presentation = user && verified() ? preferredMode(user.id) : 'pond';
      pondTheme = hostTheme;
    } else if (!verified()) presentation = 'pond';
    else if (!wasVerified && user) presentation = preferredMode(user.id);
    await loadPond(); synchronizePresentation(); renderHud();
  },
};

async function loadPond(): Promise<void> {
  const version = ++loadVersion;
  collectionError = null;
  if (!verified()) { lilypads = []; pond = null; collectionLoading = false; }
  else {
    collectionLoading = true; explorer?.update({ loading: true, error: null });
    try {
      const result = await api.pond();
      if (version !== loadVersion || disposed) return;
      lilypads = result.lilypads; pond = result.pond; baseDomain = result.baseDomain;
      pondTheme = result.pond?.theme ?? hostTheme;
    } catch (error) {
      if (version !== loadVersion || disposed) return;
      collectionError = errMsg(error);
      if (presentation === 'pond') toast(collectionError, 'error');
    } finally { if (version === loadVersion) collectionLoading = false; }
  }
  if (version !== loadVersion || disposed) return;
  applyCharacter(); updatePads(); explorer?.update({ lilypads, loading: collectionLoading, error: collectionError });
}
async function eventById(id: string): Promise<Lilypad | null> {
  const listed = lilypads.find((lp) => lp.id === id);
  if (listed) return listed;
  try {
    const detail = await api.lilypad(id), lp = detail.lilypad;
    return { ...lp, mine: lp.owner_id === user?.id, role: lp.owner_id === user?.id ? 'owner' : 'member', media_count: lp.media_count ?? 0, member_count: detail.members.length };
  } catch (error) { toast(`Could not open this lilypad: ${errMsg(error)}`, 'error'); return null; }
}
function go(next: View): void {
  if (!closePanel()) return;
  view = next;
  if (next.kind === 'pond') { if (sceneReady) scene.zoomOut(); }
  else if (next.kind === 'lilypad') {
    if (sceneReady && presentation === 'pond') scene.focusPad(next.lp.id);
    showPanel(lilypadPanel(session, next.lp, (m, all) => go({ kind: 'media', lp: next.lp, m, all }), (leafId) => go({ kind: 'leaf', lp: next.lp, leafId }), () => void loadPond(), next.section ?? 'photos', () => go({ kind: 'upload', lp: next.lp })), () => go({ kind: 'pond' }));
  } else if (next.kind === 'upload') {
    showPanel(uploadFlow(session, next.lp, () => void loadPond(), () => go({ kind: 'lilypad', lp: lilypads.find((lp) => lp.id === next.lp.id) ?? next.lp })), () => go({ kind: 'lilypad', lp: next.lp }));
  } else if (next.kind === 'media') {
    showPanel(mediaPanel(session, next.m, next.all, (m) => go({ kind: 'media', lp: next.lp, m, all: next.all }), () => go({ kind: 'lilypad', lp: next.lp }), next.lp.can_contribute !== false, next.lp.owner_id === user?.id), () => go({ kind: 'lilypad', lp: next.lp }));
  } else if (next.kind === 'leaf') {
    showPanel(leafPanel(session, next.leafId, next.lp.id, () => go({ kind: 'lilypad', lp: next.lp }), next.lp.can_contribute !== false), () => go({ kind: 'lilypad', lp: next.lp }));
  }
  renderHud();
}
function back(): void {
  if (view.kind === 'media' || view.kind === 'leaf' || view.kind === 'upload') go({ kind: 'lilypad', lp: view.lp });
  else if (view.kind === 'lilypad') go({ kind: 'pond' });
}
function openCreate(): void {
  if (!verified()) return;
  showPanel(createLilypadPanel(session, baseDomain, (id, action) => {
    void (async () => {
      await session.refresh();
      // The first callback only refreshes data/quota; the success invitation stays open.
      if (!id || !action) return;
      const lp = await eventById(id); if (!lp) return;
      if (action === 'view') { if (setPresentation('explorer')) go({ kind: 'lilypad', lp }); }
      else go({ kind: 'upload', lp });
    })().catch((error) => toast(errMsg(error), 'error'));
  }));
}

function showEntry(content: HTMLElement): void {
  introHandle?.walkOff();
  if (showPanel(content, () => introHandle?.walkOff()) && sceneReady) introHandle = scene.introCharacter();
}
function showInviteCode(): void {
  if (!joinTarget) return;
  const target = joinTarget;
  const error = h('p', { class: 'error', role: 'alert' });
  const code = h('input', { id: 'invite-event-code', required: true, autocomplete: 'off', placeholder: 'Code from your invitation' });
  const submit = h('button', { type: 'submit', class: 'btn' }, 'Open event');
  const form = h('form', { 'data-sign': '1', onSubmit: async (event: SubmitEvent) => {
    event.preventDefault(); if (!code.value.trim()) { code.focus(); return; }
    submit.disabled = true; form.dataset.busy = 'true'; error.textContent = '';
    try {
      await api.joinLilypad(target.id, code.value.trim()); await session.refresh();
      form.dataset.busy = 'false';
      const lp = await eventById(target.id);
      if (lp) { clearInvitation(); go({ kind: 'lilypad', lp }); }
    } catch (problem) { error.textContent = errMsg(problem); }
    finally { form.dataset.busy = 'false'; submit.disabled = false; }
  } }, h('h2', {}, `Join “${target.title}”`), h('p', { class: 'muted' }, `Enter the event code ${target.owner_first} shared. This is different from an email or text verification code.`), h('label', { for: code.id }, 'Event code'), code, error, h('div', { class: 'row' }, submit));
  showEntry(form);
}
function finishAuth(): void {
  introHandle?.walkOff();
  if (!user) return;
  if (!verified()) { showEntry(verifyPanel(session, null, finishAuth)); return; }
  const proceed = () => {
    if (!joinTarget) { synchronizePresentation(); renderHud(); return; }
    const lp = lilypads.find((item) => item.id === joinTarget!.id);
    if (lp) { clearInvitation(); go({ kind: 'lilypad', lp }); }
    else { rememberInvitation(); showInviteCode(); }
  };
  if (retention && !retention.exempt && !askedConfirm && (retention.needsConfirm || new URLSearchParams(location.search).has('confirm'))) {
    askedConfirm = true;
    showPanel(confirmContactPanel(session, retention, proceed), proceed);
  } else proceed();
}
function renderHud(): void {
  const crumbs = document.getElementById('crumbs')!, actions = document.getElementById('top-right')!;
  const items: HTMLElement[] = [h('button', { type: 'button', onClick: () => go({ kind: 'pond' }) }, verified() ? 'My lilypads' : pond ? `🌊 ${pond.name}` : '🌊 Pond')];
  if (view.kind !== 'pond') {
    const lp = view.lp;
    items.push(h('span', { class: 'sep' }, '›'), h('button', { type: 'button', onClick: () => go({ kind: 'lilypad', lp }) }, lp.title));
    if (view.kind === 'upload') items.push(h('span', { class: 'sep' }, '›'), h('span', {}, 'Add photos'));
    if (view.kind === 'leaf') items.push(h('span', { class: 'sep' }, '›'), h('span', {}, 'Collage'));
    if (view.kind === 'media') items.push(h('span', { class: 'sep' }, '›'), h('span', {}, view.m.kind === 'video' ? 'Video' : 'Photo'));
  }
  crumbs.replaceChildren(...items);
  if (!user) {
    actions.replaceChildren(soundControls, motionControls, joinTarget ? h('button', { type: 'button', onClick: () => showEntry(joinPanel(session, joinTarget!, finishAuth)) }, 'Join with code') : h('button', { type: 'button', onClick: () => showEntry(loginPanel(session, finishAuth)) }, 'Sign in'));
    measureHud(); return;
  }
  if (!verified()) {
    actions.replaceChildren(soundControls, motionControls, h('button', { type: 'button', onClick: () => showEntry(verifyPanel(session, null, finishAuth)) }, 'Finish joining'), h('button', { type: 'button', class: 'btn secondary', onClick: () => showPanel(accountPanel()) }, 'Account'));
    measureHud(); return;
  }
  const modes = h('div', { class: 'presentation-switch', role: 'group', 'aria-label': 'Event collection view' },
    h('button', { type: 'button', 'aria-pressed': String(presentation === 'pond'), onClick: () => setPresentation('pond') }, 'Pond'),
    h('button', { type: 'button', 'aria-pressed': String(presentation === 'explorer'), onClick: () => setPresentation('explorer') }, 'Explorer'));
  actions.replaceChildren(modes, ...(presentation === 'pond' ? [soundControls, motionControls] : []), h('button', { type: 'button', onClick: openCreate }, 'Create lilypad'), h('button', { type: 'button', class: 'btn secondary', title: `${user.first_name} ${user.last_name}`, onClick: () => showPanel(accountPanel()) }, 'Account'));
  measureHud();
}

function accountPanel(): HTMLElement {
  const current = user!;
  if (!verified()) return h('div', {}, h('h2', {}, `${current.first_name} ${current.last_name}`), h('p', { class: 'muted' }, `${current.email} · ${current.phone_e164}`), h('p', {}, 'Confirm your email and phone to finish joining.'),
    h('div', { class: 'row' }, h('button', { class: 'btn', onClick: () => showEntry(verifyPanel(session, null, finishAuth)) }, 'Finish joining'), h('button', { class: 'btn secondary', onClick: async () => { if (!closePanel()) return; await api.logout(); await session.refresh(); } }, 'Sign out')));
  return h('div', {}, h('h2', {}, `${current.first_name} ${current.last_name}`), h('p', { class: 'muted' }, `${current.email} · ${current.phone_e164} · seat: ${current.tier}${ent?.subscriptionActive ? ' (active)' : current.tier === 'premium' ? ' (lapsed)' : ''}`),
    ent?.paid ? h('p', {}, `Videos up to ${ent.maxVideoSeconds / 60} min. ${Math.max(0, ent.lilypadsPerDay - ent.lilypadsCreatedToday)} lilypad(s) left today.`) : h('div', { class: 'row' }, h('button', { class: 'btn', onClick: () => showPanel(subscribePanel(session)) }, 'Premium seat')),
    h('h3', {}, 'Your uploads'), h('p', { class: 'muted' }, 'Download everything you have uploaded, at full quality, as one zip. Available on every seat.'),
    h('div', { class: 'row' }, h('a', { class: 'btn', href: api.exportUrl, download: '' }, 'Download all my uploads')),
    retention && !retention.exempt && retention.deleteAt ? h('p', { class: 'muted' }, `Sign in and confirm your details at least every six months (next deadline ${new Date(retention.deleteAt).toLocaleDateString()}).`) : null,
    h('div', { class: 'row' }, h('button', { class: 'btn secondary', onClick: () => showPanel(subscribePanel(session)) }, 'Billing'), h('button', { class: 'btn secondary', onClick: () => showPanel(messagesPanel(session)) }, 'Messages'),
      pond ? h('button', { class: 'btn secondary', onClick: () => showPanel(sharingPanel(session, pond!.id, lilypads)) }, 'Sharing') : null,
      current.tier === 'admin' ? h('a', { class: 'btn secondary', href: '/admin.html' }, 'Service administration') : null,
      h('button', { class: 'btn secondary', onClick: async () => { if (!closePanel()) return; await api.logout(); await session.refresh(); go({ kind: 'pond' }); } }, 'Sign out')),
    h('h3', {}, 'Other ponds'), h('p', { class: 'muted' }, 'Want to create lilypads on a friend’s pond? Ask its owner first.'),
    ...[...new Map(lilypads.filter((lp) => !lp.mine).map((lp) => [lp.pond_id, lp])).values()].map((lp) => h('div', { class: 'item-row' }, h('span', {}, `${lp.owner_name}'s pond`), h('button', { class: 'btn small secondary', onClick: () => showPanel(requestCreatePanel(lp.pond_id, `${lp.owner_name}'s pond`)) }, 'Ask to create here'))));
}

async function boot(): Promise<void> {
  rules = await api.rules();
  const slugFromPath = location.pathname.match(/^\/p\/([a-z0-9-]+)/)?.[1];
  try {
    let resolved = await api.resolve(slugFromPath);
    if (!resolved.lilypad && !slugFromPath) {
      try {
        const saved = JSON.parse(sessionStorage.getItem('lilypad.pending-invite.v1') ?? 'null') as { id?: string; slug?: string; at?: number } | null;
        if (saved && typeof saved.slug === 'string' && /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(saved.slug) && typeof saved.at === 'number' && Date.now() - saved.at < 24 * 60 * 60 * 1000) {
          const pending = await api.resolve(saved.slug);
          if (pending.lilypad?.id === saved.id) resolved = pending;
        }
      } catch { /* An unusable saved intent never prevents ordinary sign-in. */ }
    }
    joinTarget = resolved.lilypad; baseDomain = resolved.baseDomain; hostTheme = pondTheme = resolved.pondTheme ?? null;
    rememberInvitation();
  } catch { /* This host need not resolve to an individual event. */ }
  await session.refresh();
  scene.onPadTap((id) => {
    sound.start(); const lp = lilypads.find((item) => item.id === id);
    if (lp) go({ kind: 'lilypad', lp });
    else if (joinTarget?.id === id) { if (verified()) showInviteCode(); else if (user) showEntry(verifyPanel(session, null, finishAuth)); else showEntry(joinPanel(session, joinTarget, finishAuth)); }
  });
  window.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !event.defaultPrevented) back(); });
  window.addEventListener('pointerdown', () => { if (presentation === 'pond' && sceneReady) sound.start(); });
  if (presentation === 'pond') await ensureScene();
  if (user) { finishAuth(); }
  else {
    await ensureScene();
    if (joinTarget) showEntry(joinPanel(session, joinTarget, finishAuth));
    else showEntry(h('div', { 'data-sign': '1' }, h('h2', {}, 'Welcome to LilyPad 🪷'), h('p', {}, 'A pond for your event’s photos and videos. Everyone with the code can add to it live.'), h('p', { class: 'muted' }, 'Got an invite? Open the link you were sent (it looks like name.yourpond.com) and tap the lilypad.'),
      h('div', { class: 'row' }, h('button', { class: 'btn', onClick: () => showEntry(loginPanel(session, finishAuth)) }, 'Sign in'), h('a', { class: 'btn secondary', href: '/pond.html' }, 'Explore the pond'))));
  }
  document.body.dataset.ready = 'true';
}
window.addEventListener('pagehide', (event) => {
  if (event.persisted) return;
  disposed = true; hudObserver.disconnect(); window.removeEventListener('resize', measureHud);
  explorer?.cleanup(); scene.destroy(); sound.destroy();
});
boot().catch((error) => { console.error(error); toast(`Could not start: ${errMsg(error)}`, 'error'); });
